import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Shared shell for public content pages (About, Contact, HowItWorks,
 * ScoringRules, Blog). Mirrors the look of Terms/Privacy/Credits so the
 * whole set feels like one site — AdSense reviewers reward consistency.
 */
export function ContentPageShell({
  children,
  backLabel = "Zpět",
}: {
  children: ReactNode;
  backLabel?: string;
}) {
  return (
    <div className="min-h-screen bg-gradient-hero text-primary-foreground">
      <header className="container mx-auto px-4 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-bold text-lg">
          <img src="/icon-512.png?v=2" alt="Tipomatic" className="h-8 w-8 rounded-lg object-cover" />
          Tipomatic
        </Link>
        <Button asChild variant="ghost" className="text-pitch-cream hover:bg-white/10 hover:text-pitch-cream">
          <Link to="/">
            <ChevronLeft className="mr-1 h-4 w-4" /> {backLabel}
          </Link>
        </Button>
      </header>
      <main className="container mx-auto px-4 py-10 max-w-3xl">{children}</main>
      <footer className="border-t border-pitch py-8 text-center text-pitch-cream/40 text-sm">
        <p>© {new Date().getFullYear()} Tipomatic</p>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          <Link to="/about" className="hover:text-pitch-cream">O nás</Link>
          <span className="text-pitch-cream/20">·</span>
          <Link to="/how-it-works" className="hover:text-pitch-cream">Jak to funguje</Link>
          <span className="text-pitch-cream/20">·</span>
          <Link to="/scoring-rules" className="hover:text-pitch-cream">Bodování</Link>
          <span className="text-pitch-cream/20">·</span>
          <Link to="/blog" className="hover:text-pitch-cream">Blog</Link>
          <span className="text-pitch-cream/20">·</span>
          <Link to="/contact" className="hover:text-pitch-cream">Kontakt</Link>
          <span className="text-pitch-cream/20">·</span>
          <Link to="/terms" className="hover:text-pitch-cream">Podmínky</Link>
          <span className="text-pitch-cream/20">·</span>
          <Link to="/privacy" className="hover:text-pitch-cream">Soukromí</Link>
          <span className="text-pitch-cream/20">·</span>
          <Link to="/credits" className="hover:text-pitch-cream">Kdo za tím stojí</Link>
        </div>
      </footer>
    </div>
  );
}

export function Prose({ children }: { children: ReactNode }) {
  return (
    <div className="space-y-5 text-pitch-cream/85 text-[15px] leading-relaxed [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-pitch-cream [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-pitch-cream [&_h3]:mt-6 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-1 [&_a]:text-primary [&_a]:underline-offset-2 hover:[&_a]:underline [&_strong]:text-pitch-cream">
      {children}
    </div>
  );
}
